import {
  ERROR_MESSAGES,
  PDF_MEDIA_TYPE,
  PDF_TO_DOCX_MAX_FILE_SIZE,
  type ErrorCode,
} from "@wendo/contracts";

const PDF_HEADER = "%PDF-";

export class ClientFileError extends Error {
  constructor(readonly code: ErrorCode) {
    super(ERROR_MESSAGES[code]);
    this.name = "ClientFileError";
  }
}

async function readPrefix(file: File, length: number): Promise<Uint8Array> {
  const blob = file.slice(0, length);
  if (typeof blob.arrayBuffer === "function") {
    return new Uint8Array(await blob.arrayBuffer());
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(new Uint8Array(reader.result));
      } else {
        reject(new ClientFileError("INVALID_UPLOAD"));
      }
    });
    reader.addEventListener("error", () =>
      reject(new ClientFileError("INVALID_UPLOAD")),
    );
    reader.readAsArrayBuffer(blob);
  });
}

export async function validatePdfFile(file: File): Promise<void> {
  if (!file.name.toLowerCase().endsWith(".pdf")) {
    throw new ClientFileError("UNSUPPORTED_FILE");
  }
  if (file.type.length > 0 && file.type !== PDF_MEDIA_TYPE) {
    throw new ClientFileError("UNSUPPORTED_FILE");
  }
  if (file.size <= 0) throw new ClientFileError("INVALID_UPLOAD");
  if (file.size > PDF_TO_DOCX_MAX_FILE_SIZE) {
    throw new ClientFileError("FILE_TOO_LARGE");
  }
  const prefix = await readPrefix(file, PDF_HEADER.length);
  if (new TextDecoder().decode(prefix) !== PDF_HEADER) {
    throw new ClientFileError("INVALID_UPLOAD");
  }
}

export type UploadFile = (
  url: string,
  file: File,
  onProgress: (progress: number) => void,
) => Promise<void>;

export const uploadFileToR2: UploadFile = (url, file, onProgress) =>
  new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", url);
    request.setRequestHeader("Content-Type", PDF_MEDIA_TYPE);
    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });
    request.addEventListener("load", () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress(100);
        resolve();
      } else {
        reject(new ClientFileError("INVALID_UPLOAD"));
      }
    });
    request.addEventListener("error", () =>
      reject(new ClientFileError("INVALID_UPLOAD")),
    );
    request.addEventListener("abort", () =>
      reject(new ClientFileError("INVALID_UPLOAD")),
    );
    request.send(file);
  });
